import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, MapPin, Home } from "lucide-react";
import useBooking from "../hooks/stores/useBooking";
import { slotService } from "../services";
import { convertLocalTimeToUTC } from "../utils/timeUtils";

export default function InPersonBooking() {
  const navigate = useNavigate();
  const { saveBookingData } = useBooking();
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("10:00");
  const [selectedLocation, setSelectedLocation] = useState("studio");
  const [bookedSlots, setBookedSlots] = useState([]);

  // Updated time slots based on 3-hour duration with 30-minute breaks
  const defaultTimeSlots = [
    { time: "10:00", endTime: "13:00", available: true },    // 10:00-13:00 (3 hours)
    { time: "13:30", endTime: "16:30", available: true },    // 13:30-16:30 (3 hours) + 30 min break
    { time: "17:00", endTime: "20:00", available: true },    // 17:00-20:00 (3 hours) + 30 min break
  ];

  // Utility functions - moved to top to avoid hoisting issues
  const formatDateForDisplay = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric'
    });
  };

  const formatDateForAPI = (date) => {
    // Use local date to avoid timezone conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getMonthYearDisplay = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Generate calendar dates with 2-day minimum future booking
  const generateCalendarDates = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const dates = [];
    const today = new Date();
    const minBookingDate = new Date(today);
    minBookingDate.setDate(today.getDate() + 2); // 2 days from today
    minBookingDate.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < 35; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
          // Check if this date has any available slots
    const dateStr = formatDateForAPI(date);
    const bookedTimesForDate = bookedSlots
      .filter(slot => slot.slot_date === dateStr)
      .map(slot => slot.converted_time ? slot.converted_time.substring(0, 5) : slot.slot_time.substring(0, 5)); // Use converted_time (local time) or fallback to slot_time
      
      const availableSlotsForDate = defaultTimeSlots.filter(
        slot => !bookedTimesForDate.includes(slot.time)
      );
      
      const isFullyBooked = availableSlotsForDate.length === 0 && date >= minBookingDate && date.getMonth() === month;
      
      dates.push({
        date: date,
        day: date.getDate(),
        isCurrentMonth: date.getMonth() === month,
        isPastDate: date < minBookingDate,
        isToday: date.toDateString() === today.toDateString(),
        isMinBookingDate: date.toDateString() === minBookingDate.toDateString(),
        isFullyBooked: isFullyBooked
      });
    }
    
    return dates;
  };

  const [calendarDates, setCalendarDates] = useState(generateCalendarDates(currentDate));

  useEffect(() => {
    setCalendarDates(generateCalendarDates(currentDate));
  }, [currentDate, bookedSlots]);

  // Fetch all slots when component mounts
  useEffect(() => {
    const fetchAllSlots = async () => {
      try {
        const response = await slotService.getAllSlots();
        console.log('All slots data:', response);
        
        if (response && response.slots) {
          setBookedSlots(response.slots);
          console.log('Processed booked slots:', response.slots.map(slot => ({
            date: slot.slot_date,
            slot_time: slot.slot_time,
            converted_time: slot.converted_time,
            local_time_display: slot.converted_time ? slot.converted_time.substring(0, 5) : slot.slot_time.substring(0, 5)
          })));
        }
      } catch (error) {
        console.error('Failed to fetch slots:', error);
      }
    };

    fetchAllSlots();
  }, []);

  // Get available time slots for selected date
  const getAvailableTimeSlotsForDate = (date) => {
    if (!date) return defaultTimeSlots;
    
    const dateStr = formatDateForAPI(date);
    const bookedTimesForDate = bookedSlots
      .filter(slot => slot.slot_date === dateStr)
      .map(slot => slot.converted_time ? slot.converted_time.substring(0, 5) : slot.slot_time.substring(0, 5)); // Use converted_time (local time) or fallback to slot_time
    
    return defaultTimeSlots.map(slot => ({
      ...slot,
      available: !bookedTimesForDate.includes(slot.time)
    }));
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const handleDateSelect = (dateObj) => {
    // Only allow selection of future dates (2+ days from today) in current month that are not fully booked
    if (dateObj.isPastDate || !dateObj.isCurrentMonth || dateObj.isFullyBooked) {
      return;
    }
    setSelectedDate(dateObj.date);
    
    // Reset selected time if it's not available for this date
    const availableSlots = getAvailableTimeSlotsForDate(dateObj.date);
    const isCurrentTimeAvailable = availableSlots.find(slot => slot.time === selectedTime && slot.available);
    
    if (!isCurrentTimeAvailable) {
      const firstAvailableSlot = availableSlots.find(slot => slot.available);
      if (firstAvailableSlot) {
        setSelectedTime(firstAvailableSlot.time);
      }
    }
  };

  const handleTimeSlotSelect = (time) => {
    setSelectedTime(time);
  };

  const handleSlotSelection = () => {
    if (!selectedDate) {
      alert('Please select a date');
      return;
    }

    const availableSlots = getAvailableTimeSlotsForDate(selectedDate);
    const selectedSlot = availableSlots.find(slot => slot.time === selectedTime);
    
    if (!selectedSlot || !selectedSlot.available) {
      alert('Selected time slot is not available');
      return;
    }

    // Store booking data in context with UTC conversion
    const slotData = {
      slot_date: formatDateForAPI(selectedDate),
      slot_time: convertLocalTimeToUTC(selectedDate, selectedTime), // UTC time
      converted_time: selectedTime, // Local time as originally selected
      duration: 180,
      location: selectedLocation
    };

    console.log('Storing booking data:', slotData);
    console.log('Local time selected:', selectedTime);
    console.log('UTC time converted:', convertLocalTimeToUTC(selectedDate, selectedTime));
    console.log('User timezone:', Intl.DateTimeFormat().resolvedOptions().timeZone);
    
    saveBookingData(slotData);
    
    // Navigate to signup where user will register first
    navigate('/signup');
  };

  const getMinBookingMessage = () => {
    const today = new Date();
    const minDate = new Date(today);
    minDate.setDate(today.getDate() + 2);
    return `Booking available from ${formatDateForDisplay(minDate)} onwards`;
  };

  const availableTimeSlotsForSelectedDate = getAvailableTimeSlotsForDate(selectedDate);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-[2.625rem] font-semibold text-foreground mb-4">Book Your In-Person Recording</h1>
          <p className="text-base font-semibold text-muted-foreground">Choose a convenient time and location</p>
          <p className="text-sm font-normal text-orange-600 mt-2">{getMinBookingMessage()}</p>
        </div>

        {/* Location Selection First */}
        <div className="mb-8">
          <h2 className="text-base font-semibold text-foreground mb-4">Where would you like to meet?</h2>
          <div className="grid grid-cols-2 gap-4">
            <button 
              className="p-6 bg-primary text-white rounded-lg flex flex-col items-center justify-center gap-2"
              onClick={() => setSelectedLocation('studio')}
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <MapPin className="text-white" />
              </div>
              <span className="text-base font-normal">Our Studio</span>
            </button>

            <button 
              className="p-6 bg-secondary border border-secondary-border text-foreground rounded-lg flex flex-col items-center justify-center gap-2 opacity-50 cursor-not-allowed"
              disabled
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <Home className="text-foreground" />
              </div>
              <span className="text-base font-normal">Your Home</span>
            </button>
          </div>
        </div>

        <div className="bg-secondary rounded-xl border border-secondary-border p-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Calendar/Slot Selection */}
            <div>
              <h3 className="text-base font-semibold text-foreground mb-6">Choose Your Preferred Time</h3>
              
              {/* Calendar Header */}
              <div className="flex items-center justify-between mb-6">
                <button 
                  className="p-2 hover:bg-muted rounded-lg"
                  onClick={() => navigateMonth(-1)}
                >
                  <ChevronLeft className="text-muted-foreground" size={20} />
                </button>
                <h4 className="text-base font-normal text-foreground">{getMonthYearDisplay(currentDate)}</h4>
                <button 
                  className="p-2 hover:bg-muted rounded-lg"
                  onClick={() => navigateMonth(1)}
                >
                  <ChevronRight className="text-muted-foreground" size={20} />
                </button>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1 mb-4">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">{day}</div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {calendarDates.slice(0, 35).map((dateObj, index) => (
                  <div
                    key={index}
                    className={`text-center py-2 text-sm rounded-lg transition-colors ${
                      selectedDate && selectedDate.toDateString() === dateObj.date.toDateString()
                        ? "bg-primary text-primary-foreground cursor-pointer" 
                        : dateObj.isPastDate || !dateObj.isCurrentMonth
                          ? "text-muted-foreground cursor-not-allowed opacity-50"
                          : dateObj.isFullyBooked
                            ? "text-red-500 cursor-not-allowed opacity-50 line-through"
                            : dateObj.isToday
                              ? "bg-secondary border border-secondary-border text-foreground hover:bg-secondary/90 cursor-pointer"
                              : dateObj.isMinBookingDate
                                ? "bg-secondary border border-secondary-border text-foreground hover:bg-secondary/90 cursor-pointer"
                                : "text-foreground hover:bg-secondary border border-secondary-border cursor-pointer"
                    }`}
                    onClick={() => handleDateSelect(dateObj)}
                    title={
                      dateObj.isPastDate ? "Booking must be at least 2 days in advance" 
                      : dateObj.isFullyBooked ? "All time slots are booked for this date"
                      : ""
                    }
                  >
                    {dateObj.day}
                  </div>
                ))}
              </div>
              </div>

              {/* Time Slots */}
            <div>
              <h5 className="text-base font-normal text-foreground mb-4">
                  Available Times - {selectedDate ? formatDateForDisplay(selectedDate) : 'Select a date'}
                </h5>
              <div className="space-y-3">
                  {availableTimeSlotsForSelectedDate.map(slot => (
                    <button
                      key={slot.time}
                    className={`w-full p-4 rounded-lg border transition-colors ${
                      !selectedDate || !slot.available
                        ? 'bg-secondary border-secondary-border text-muted-foreground opacity-50 cursor-not-allowed'
                        : selectedTime === slot.time
                          ? 'bg-primary border-primary-border text-white'
                          : 'bg-secondary border-secondary-border text-foreground hover:bg-secondary/90'
                    }`}
                      onClick={() => slot.available && handleTimeSlotSelect(slot.time)}
                      disabled={!selectedDate || !slot.available}
                      title={!slot.available ? "This time slot is already booked" : `${slot.time} - ${slot.endTime} (3 hours)`}
                    >
                    <div className="flex flex-col items-center">
                      <span className={`text-base font-normal ${!slot.available ? 'line-through' : ''}`}>{slot.time}</span>
                      {!slot.available && (
                        <span className="text-sm text-red-500 mt-1">Booked</span>
                      )}
                    </div>
                    </button>
                  ))}
                {selectedDate && availableTimeSlotsForSelectedDate.every(slot => !slot.available) && (
                  <div className="text-center py-4 text-red-500">
                    All time slots are booked for this date. Please select another date.
                  </div>
                )}
              </div>
            </div>
                </div>

          {/* Booking Summary */}
          <div>
              {selectedDate && (
              <div className="mt-8 p-4 bg-secondary border border-secondary-border rounded-lg">
                  <h6 className="text-base font-semibold text-foreground mb-2">Booking Summary</h6>
                  <p className="text-sm font-normal text-muted-foreground">Date: {formatDateForDisplay(selectedDate)}</p>
                  <p className="text-sm font-normal text-muted-foreground">Time: {selectedTime} - {availableTimeSlotsForSelectedDate.find(s => s.time === selectedTime)?.endTime}</p>
                  <p className="text-sm font-normal text-muted-foreground">Duration: 3 hours</p>
                  <p className="text-sm font-normal text-muted-foreground">Location: {selectedLocation === 'home' ? 'Your Home' : 'Our Studio'}</p>
                </div>
              )}

              <button 
                onClick={handleSlotSelection} 
              className="w-full py-4 bg-primary text-white rounded-lg text-base font-medium hover:bg-primary/90 transition-colors flex items-center justify-center mt-4"
                disabled={!selectedDate || availableTimeSlotsForSelectedDate.every(slot => !slot.available)}
              >
                Continue to Registration
              </button>
          </div>
        </div>
      </div>
    </div>
  );
} 